<template>
  <div class="recensement-form-container">
    <!-- Header -->
    <div class="header-section">
      <h1 class="page-title">
        <i class="fas fa-user-plus"></i>
        Enregistrement d'un Nouvel Usager
      </h1>
      <p class="page-subtitle">Complétez les informations de l'usager à recenser</p>
    </div>

    <!-- Form Section -->
    <div class="form-section">
      <div class="form-container">
        <Form ref="recensementForm" @submit="submitForm" :validation-schema="recensementSchema">


          <!-- Informations personnelles -->
          <div class="form-section-title">
            <h3><i class="fas fa-user"></i> Informations personnelles</h3>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label required">Nom</label>
              <Field 
                name="nom"
                type="text" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Nom de famille"
                v-model="formData.nom"
              />
              <ErrorMessage name="nom" class="text-danger"/>
            </div>
            <div class="form-group">
              <label class="form-label required">Prénom</label>
              <Field 
                name="prenom"
                type="text" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Prénom"
                v-model="formData.prenom"
              />
              <ErrorMessage name="prenom" class="text-danger"/>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Date de naissance</label>
              <Field 
                name="dateNaissance"
                type="date" 
                class="form-control shadow-none fs-md-15 text-black"
                v-model="formData.dateNaissance"
              />
              <ErrorMessage name="dateNaissance" class="text-danger"/>
            </div>
            <div class="form-group">
              <label class="form-label">Sexe</label>
              <Field name="sexe" v-slot="{ field }">
                <Multiselect
                  v-bind="field"
                  :options="sexeOptions"
                  :searchable="false"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :preserve-search="false"
                  placeholder="Sélectionnez le sexe"
                  class="form-control shadow-none fs-md-15 text-black"
                  v-model="formData.sexe"
                />
              </Field>
              <ErrorMessage name="sexe" class="text-danger"/>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Téléphone</label>
              <Field 
                name="telephone"
                type="tel" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Numéro de téléphone"
                v-model="formData.telephone"
              />
              <ErrorMessage name="telephone" class="text-danger"/>
            </div>
            <div class="form-group">
              <label class="form-label">Email</label>
              <Field 
                name="email"
                type="email" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Adresse email"
                v-model="formData.email"
              />
              <ErrorMessage name="email" class="text-danger"/>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Adresse</label>
            <Field 
              name="adresse"
              as="textarea"
              class="form-control shadow-none fs-md-15 text-black"
              rows="3"
              placeholder="Adresse complète"
              v-model="formData.adresse"
            />
            <ErrorMessage name="adresse" class="text-danger"/>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Lieu de naissance</label>
              <Field 
                name="lieuNaissance"
                type="text" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Lieu de naissance"
                v-model="formData.lieuNaissance"
              />
              <ErrorMessage name="lieuNaissance" class="text-danger"/>
            </div>
            <div class="form-group">
              <label class="form-label">Nationalité</label>
              <Field 
                name="nationalite"
                type="text" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Nationalité"
                v-model="formData.nationalite"
              />
              <ErrorMessage name="nationalite" class="text-danger"/>
            </div>
          </div>

          <!-- Informations professionnelles -->
          <div class="form-section-title">
            <h3><i class="fas fa-briefcase"></i> Informations professionnelles</h3>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">NPI</label>
              <Field 
                name="npiUsager"
                type="text" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Numéro d'identification professionnelle"
                v-model="formData.npiUsager"
              />
              <ErrorMessage name="npiUsager" class="text-danger"/>
            </div>
            <div class="form-group">
              <label class="form-label">IFU</label>
              <Field 
                name="ifu"
                type="text" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Identifiant fiscal unique"
                maxlength="13"
                v-model="formData.ifu"
              />
              <ErrorMessage name="ifu" class="text-danger"/>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Raison sociale</label>
              <Field 
                name="raisonSocial"
                type="text" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Raison sociale de l'entreprise"
                v-model="formData.raisonSocial"
              />
              <ErrorMessage name="raisonSocial" class="text-danger"/>
            </div>
            <div class="form-group">
              <label class="form-label">Dénomination</label>
              <Field 
                name="denomination"
                type="text" 
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Dénomination commerciale"
                v-model="formData.denomination"
              />
              <ErrorMessage name="denomination" class="text-danger"/>
            </div>
          </div>

          <!-- Informations d'emplacement -->
          <div class="form-section-title">
            <h3><i class="fas fa-map-marker-alt"></i> Informations d'emplacement</h3>
          </div>

          <div class="form-group">
            <label class="form-label">Code emplacement</label>
            <Field 
              name="codeemp"
              type="text" 
              class="form-control shadow-none fs-md-15 text-black"
              placeholder="Code de l'emplacement"
              v-model="formData.codeemp"
            />
            <ErrorMessage name="codeemp" class="text-danger"/>
          </div>

          <!-- Date de recensement -->
          <div class="form-group">
            <label class="form-label">Date de recensement</label>
            <Field 
              name="dateRecensement"
              type="date" 
              class="form-control shadow-none fs-md-15 text-black"
              v-model="formData.dateRecensement"
            />
            <ErrorMessage name="dateRecensement" class="text-danger"/>
          </div>

          <!-- Action Buttons -->
          <div class="form-actions">
            <button 
              type="button" 
              @click="goBack" 
              class="btn btn-secondary"
              :disabled="isSubmitting"
            >
              <i class="fas fa-arrow-left"></i>
              Retour
            </button>
            <button 
              type="submit" 
              class="btn btn-primary"
              :disabled="isSubmitting"
            >
              <i v-if="isSubmitting" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-save"></i>
              {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}
            </button>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Form, Field, ErrorMessage } from 'vee-validate'
import * as Yup from 'yup'
import Swal from 'sweetalert2'
import ApiService from '@/services/ApiService'
import Multiselect from '@vueform/multiselect'

export default defineComponent({
  name: 'RecensementForm',
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect
  },

  setup() {
    // Router and Route
    const router = useRouter()
    const route = useRoute()

    // Refs
    const isSubmitting = ref(false)
    const recensementForm = ref(null)

    // Form data
    const formData = reactive({
      nom: '',
      prenom: '',
      dateNaissance: '',
      sexe: '',
      telephone: '',
      email: '',
      adresse: '',
      lieuNaissance: '',
      nationalite: '',
      npiUsager: '',
      ifu: '',
      raisonSocial: '',
      denomination: '',
      codeemp: '',
      dateRecensement: new Date().toISOString().split('T')[0],
      codeScanne: ''
    })

    // Validation schema with Yup
    const recensementSchema = Yup.object().shape({
      nom: Yup.string()
        .required('Le nom est obligatoire')
        .min(2, 'Le nom doit contenir au moins 2 caractères')
        .max(50, 'Le nom ne peut pas dépasser 50 caractères')
        .matches(/^[a-zA-ZÀ-ÿ\s]+$/, 'Le nom ne peut contenir que des lettres et des espaces'),
      
      prenom: Yup.string()
        .required('Le prénom est obligatoire')
        .min(2, 'Le prénom doit contenir au moins 2 caractères')
        .max(50, 'Le prénom ne peut pas dépasser 50 caractères')
        .matches(/^[a-zA-ZÀ-ÿ\s]+$/, 'Le prénom ne peut contenir que des lettres et des espaces'),
      
      dateNaissance: Yup.date()
        .notRequired()
        .max(new Date(), 'La date de naissance ne peut pas être dans le futur')
        .nullable(),
      
      sexe: Yup.string()
        .notRequired()
        .oneOf(['Masculin', 'Féminin', 'Autre'], 'Sexe invalide')
        .nullable(),
      
      telephone: Yup.string()
        .notRequired()
        .matches(/^[0-9+\-\s()]+$/, 'Format de téléphone invalide')
        .nullable(),
      
      email: Yup.string()
        .notRequired()
        .email('Format d\'email invalide')
        .nullable(),
      
      adresse: Yup.string()
        .max(200, 'L\'adresse ne peut pas dépasser 200 caractères')
        .nullable(),
      
      lieuNaissance: Yup.string()
        .notRequired()
        .max(100, 'Le lieu de naissance ne peut pas dépasser 100 caractères')
        .nullable(),
      
      nationalite: Yup.string()
        .notRequired()
        .max(50, 'La nationalité ne peut pas dépasser 50 caractères')
        .nullable(),
      
      npiUsager: Yup.string()
        .notRequired()
        .matches(/^[0-9]{10}$/, 'Le NPI doit contenir exactement 10 chiffres')
        .nullable(),
      
      ifu: Yup.string()
        .notRequired()
        .matches(/^[0-9]{13}$/, 'L\'IFU doit contenir exactement 13 chiffres')
        .nullable(),
      
      raisonSocial: Yup.string()
        .notRequired()
        .max(100, 'La raison sociale ne peut pas dépasser 100 caractères')
        .nullable(),
      
      denomination: Yup.string()
        .notRequired()
        .max(100, 'La dénomination ne peut pas dépasser 100 caractères')
        .nullable(),
      
      codeemp: Yup.string()
        .notRequired()
        .max(20, 'Le code emplacement ne peut pas dépasser 20 caractères')
        .nullable(),
      
      dateRecensement: Yup.date()
        .required('La date de recensement est obligatoire')
        .max(new Date(), 'La date de recensement ne peut pas être dans le futur')
    })

    // Methods
    const submitForm = async (values: any, { resetForm }: any) => {
      isSubmitting.value = true

      try {
        const response = await ApiService.post('/recensement-usager', values)
        
        if (response.data.success) {
          await Swal.fire({
            title: 'Succès !',
            text: 'L\'usager a été enregistré avec succès.',
            icon: 'success',
            confirmButtonText: 'OK'
          })
          
          // Rediriger vers la page de recensement
          router.push({ name: 'RecensementUsager' })
        } else {
          throw new Error(response.data.message || 'Erreur lors de l\'enregistrement')
        }
      } catch (error: any) {
        console.error('Erreur lors de l\'enregistrement:', error)
        showError(
          error.response?.data?.message || 
          'Erreur lors de l\'enregistrement de l\'usager'
        )
      } finally {
        isSubmitting.value = false
      }
    }

    const goBack = () => {
      router.go(-1)
    }

    const showError = (message: string) => {
      Swal.fire({
        title: 'Erreur',
        text: message,
        icon: 'error',
        confirmButtonText: 'OK'
      })
    }

    // Lifecycle
    onMounted(() => {
      // Récupérer le code depuis les paramètres de l'URL
      if (route.query.code) {
        formData.codeScanne = route.query.code as string
      }
    })

    // Options pour le multiselect
    const sexeOptions = [
      { value: 'Masculin', label: 'Masculin' },
      { value: 'Féminin', label: 'Féminin' },
      { value: 'Autre', label: 'Autre' }
    ]

    return {
      // Refs
      isSubmitting,
      recensementForm,
      formData,
      recensementSchema,
      sexeOptions,
      
      // Methods
      submitForm,
      goBack,
      showError
    }
  }
})
</script>

<style scoped>
.recensement-form-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #3f7a5d 0%, #764ba2 100%);
  padding: 20px;
}

.header-section {
  text-align: center;
  margin-bottom: 30px;
  color: white;
}

.page-title {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
}

.form-section {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 60vh;
}

.form-container {
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  max-width: 800px;
  width: 100%;
  margin-bottom: 40px;
}

.recensement-form {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-section-title {
  margin: 30px 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #f8f9fa;
}

.form-section-title h3 {
  margin: 0;
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
}

.form-section-title i {
  color: #007bff;
  margin-right: 10px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group.code-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  border-left: 4px solid #007bff;
}

.form-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-label.required::after {
  content: '*';
  color: #dc3545;
  margin-left: 4px;
}

.form-label i {
  color: #007bff;
  width: 16px;
}

.form-control {
  padding: 12px 15px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s;
  background: white;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0,123,255,0.1);
}

.form-control:read-only {
  background: #f8f9fa;
  color: #6c757d;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.text-danger {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.form-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid #f8f9fa;
}

.btn {
  padding: 12px 30px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 140px;
  justify-content: center;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,123,255,0.3);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  
  .form-container {
    padding: 20px;
    margin: 0 10px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 15px;
  }
  
  .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .recensement-form-container {
    padding: 10px;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .form-container {
    padding: 15px;
  }
  
  .form-section-title h3 {
    font-size: 1.1rem;
  }
}
</style>
